import api from './client'

// /api/global/option
//   GET → afd.getGlobalOption — all runtime options (kebab-case keys, string values).
//   POST {option} → afd.changeGlobalOption — apply options at runtime.
export type RPCOptions = Record<string, string>

export async function getGlobalOption(): Promise<RPCOptions> {
  // v3.18.5 fix: handler 走 okJSON {ok,data} 包装, 此前未解包 — 设置页全部
  // 字段回落默认值, 保存还会把默认值写回覆盖真实配置。
  return unwrap<RPCOptions>(await api.get('/api/global/option'))
}

export async function changeGlobalOption(option: RPCOptions): Promise<RPCOptions> {
  return api.post<RPCOptions>('/api/global/option', { option })
}

// ---------------------------------------------------------------------------
// /api/global/stat — AFD wire 协议 compatible global stat (SA4 dashboard)
//
// Backend (`internal/api/handlers_system.go: AFDGetGlobalStat`) returns every
// numeric field as a **decimal string**, wrapped by `okJSON` into `{ok, data}`.
// Everything below normalises that into plain numbers for the UI.
// ---------------------------------------------------------------------------

export interface RawGlobalStat {
  downloadSpeed: string
  uploadSpeed: string
  uploadTotal: string
  numActive: string
  numWaiting: string
  numPaused: string
  numStopped: string
  numStoppedTotal: string
}

export interface GlobalStat {
  /** bytes/s */
  downloadSpeed: number
  /** bytes/s */
  uploadSpeed: number
  /** bytes */
  uploadTotal: number
  numActive: number
  numWaiting: number
  numPaused: number
  numStopped: number
  numStoppedTotal: number
}

/** `{ok: true, data: T}` envelope used by all `tasks_webui.go` handlers. */
export interface ApiEnvelope<T> {
  ok?: boolean
  data?: T
  error?: string
  code?: number
}

/**
 * Unwrap `{ok, data}`. Handlers registered in `server.go` always wrap, but a few
 * legacy `webui.go` handlers reply with the bare object — tolerate both.
 */
export function unwrap<T>(res: unknown): T {
  if (res && typeof res === 'object' && 'data' in (res as Record<string, unknown>)) {
    const env = res as ApiEnvelope<T>
    if (env.ok === false) {
      throw new Error(env.error ?? 'request failed')
    }
    return env.data as T
  }
  return res as T
}

/** Parse a possibly-string numeric field; non-finite input degrades to 0. */
export function toNum(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0
  if (typeof v === 'string') {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

export function normalizeGlobalStat(raw: Partial<RawGlobalStat> | null | undefined): GlobalStat {
  return {
    downloadSpeed: toNum(raw?.downloadSpeed),
    uploadSpeed: toNum(raw?.uploadSpeed),
    uploadTotal: toNum(raw?.uploadTotal),
    numActive: toNum(raw?.numActive),
    numWaiting: toNum(raw?.numWaiting),
    numPaused: toNum(raw?.numPaused),
    numStopped: toNum(raw?.numStopped),
    numStoppedTotal: toNum(raw?.numStoppedTotal),
  }
}

export async function getGlobalStat(): Promise<GlobalStat> {
  const res = await api.get<ApiEnvelope<RawGlobalStat>>('/api/global/stat')
  return normalizeGlobalStat(unwrap<RawGlobalStat>(res))
}
